import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useSidebarConfig,
  NavItem,
  SidebarSection,
} from '../hooks/useSidebarConfig';
import { useAppDispatch, useAppSelector } from '@/app';
import { closeSidebar } from '../uiSlice';
import { TeamSelector } from '@/shared/ui';

interface SidebarProps {
  variant?: 'temporary' | 'persistent' | 'permanent';
  width?: number;
}

export const Sidebar: React.FC<SidebarProps> = ({ variant = 'persistent' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { sidebarOpen: open, sidebarWidth: width = 240 } = useAppSelector(
    state => state.ui
  );
  const { user } = useAppSelector(state => state.auth);
  const isJustBaatAdmin = user?.isJustBaatAdmin ?? false;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for expanded sections
  const [expandedSections, setExpandedSections] = React.useState<
    Record<string, boolean>
  >({});

  const onClose = () => {
    dispatch(closeSidebar());
  };

  // Get navigation items from layout configuration
  const { sidebarSections } = useSidebarConfig();

  // Initialize expanded sections on mount (expand all by default on desktop)
  React.useEffect(() => {
    if (!isMobile) {
      const initialExpanded: Record<string, boolean> = {};
      sidebarSections.forEach(section => {
        // Only expand sections that have submenus
        if (section.hasSubMenu) {
          initialExpanded[section.id] = true;
        }
      });
      setExpandedSections(initialExpanded);
    }
  }, [sidebarSections, isMobile]);

  const handleSectionToggle = (sectionId: string) => {
    // Only toggle sections that have submenus
    const section = sidebarSections.find(s => s.id === sectionId);
    if (section && section.hasSubMenu) {
      setExpandedSections(prev => ({
        ...prev,
        [sectionId]: !prev[sectionId],
      }));
    }
  };

  const handleItemClick = (path?: string) => {
    // Handle external URLs
    if (path && path.startsWith('http')) {
      window.open(path, '_blank');
      return;
    }

    // Handle normal navigation
    if (path) {
      navigate(path);
      // Always close sidebar on mobile after navigation
      if (isMobile) {
        onClose();
      }
    }
  };

  const isItemActive = (path?: string) => {
    if (!path) return false;
    return (
      location.pathname === path || location.pathname.startsWith(path + '/')
    );
  };

  const renderNavItem = (item: NavItem) => {
    const isActive = isItemActive(item.path);

    return (
      <ListItem key={item.id} disablePadding>
        <ListItemButton
          onClick={() => handleItemClick(item.path)}
          selected={isActive}
          sx={{
            borderRadius: 0.5,
            mx: { xs: 0.25, sm: 0.5 },
            my: { xs: 0.25, sm: 0.375 },
            py: { xs: 0.25, sm: 0.5 },
            minHeight: { xs: 32, sm: 36 },
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: { xs: 20, sm: 24 },
              color: isActive ? 'inherit' : 'text.secondary',
            }}
          >
            {React.cloneElement(item.icon as React.ReactElement, {
              fontSize: 'small',
            })}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            sx={{
              ml: -2, // Negative margin to bring text closer to icon
            }}
            primaryTypographyProps={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              fontWeight: isActive ? 500 : 400,
              lineHeight: 1.2,
              noWrap: true, // Prevent text wrapping that could cause horizontal scroll
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderSectionHeader = (section: SidebarSection) => {
    const isExpanded = expandedSections[section.id];
    const path = section?.path || section.children[0]?.path;

    // If section has no submenu, render it as a direct navigation item
    if (!section.hasSubMenu) {
      const isActive = isItemActive(path);

      return (
        <ListItem key={`header-${section.id}`} disablePadding>
          <ListItemButton
            onClick={() => handleItemClick(path)}
            selected={isActive}
            sx={{
              borderRadius: 0.5,
              mx: { xs: 0.25, sm: 0.5 },
              my: { xs: 0.25, sm: 0.375 },
              py: { xs: 0.25, sm: 0.5 },
              minHeight: { xs: 32, sm: 36 },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: { xs: 20, sm: 24 },
                color: isActive ? 'inherit' : 'text.secondary',
              }}
            >
              {React.cloneElement(section.icon as React.ReactElement, {
                fontSize: 'small',
              })}
            </ListItemIcon>
            <ListItemText
              primary={section.title}
              sx={{
                ml: -2, // Negative margin to bring text closer to icon
              }}
              primaryTypographyProps={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                fontWeight: isActive ? 500 : 400,
                lineHeight: 1.2,
                noWrap: true,
              }}
            />
          </ListItemButton>
        </ListItem>
      );
    }

    // If section has submenu, render it as an expandable section header
    return (
      <ListItem key={`header-${section.id}`} disablePadding>
        <ListItemButton
          onClick={() => handleSectionToggle(section.id)}
          sx={{
            borderRadius: 0.5,
            mx: { xs: 0.25, sm: 0.5 },
            my: { xs: 0.25, sm: 0.375 },
            py: { xs: 0.25, sm: 0.5 },
            minHeight: { xs: 28, sm: 32 },
            backgroundColor: 'grey.50',
            '&:hover': {
              backgroundColor: 'grey.100',
            },
            ...(theme.palette.mode === 'dark' && {
              backgroundColor: 'grey.800',
              '&:hover': {
                backgroundColor: 'grey.700',
              },
            }),
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: { xs: 20, sm: 24 },
              color: 'text.primary',
            }}
          >
            {React.cloneElement(section.icon as React.ReactElement, {
              fontSize: 'small',
            })}
          </ListItemIcon>
          <ListItemText
            primary={section.title}
            sx={{
              ml: -2, // Negative margin to bring text closer to icon
            }}
            primaryTypographyProps={{
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              lineHeight: 1.2,
              noWrap: true, // Prevent text wrapping that could cause horizontal scroll
            }}
          />
          {React.cloneElement(isExpanded ? <ExpandLess /> : <ExpandMore />, {
            fontSize: 'small',
          })}
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // Prevent horizontal scroll
      }}
    >
      {/* Team Selection Dropdown */}

      {isJustBaatAdmin && (
        <Box
          sx={{
            p: { xs: 0.75, sm: 1.25 },
            pb: { xs: 0.75, sm: 1 },
          }}
        >
          <TeamSelector />
        </Box>
      )}

      <Divider />

      {/* Navigation Sections */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          overflowX: 'hidden', // Prevent horizontal scroll
          py: { xs: 0.5, sm: 0.75 }, // Reduced padding to accommodate item spacing
          '&::-webkit-scrollbar': {
            width: '3px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'divider',
            borderRadius: '1.5px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'primary.main',
          },
        }}
      >
        <List
          sx={{
            px: { xs: 0.5, sm: 0.75 },
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {sidebarSections.map(section => (
            <React.Fragment key={section.id}>
              {renderSectionHeader(section)}
              {section.hasSubMenu && (
                <Collapse
                  in={expandedSections[section.id]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List
                    component="div"
                    disablePadding
                    sx={{
                      pl: { xs: 0.75, sm: 1.25 },
                      width: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    {section.children.map(item => renderNavItem(item))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
      sx={{
        width: variant === 'persistent' ? (open ? width : 0) : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          // Responsive drawer styling
          borderRight: { xs: 'none', md: '1px solid' },
          borderColor: { md: 'divider' },
          // Ensure proper z-index
          zIndex: variant === 'temporary' ? theme.zIndex.drawer : 1,
          // Ensure sidebar takes full height of container
          height: '100%',
          position: 'relative',
          // Prevent horizontal scroll
          overflow: 'hidden',
          // Add backdrop blur for mobile
          ...(variant === 'temporary' && {
            '& + .MuiBackdrop-root': {
              backdropFilter: 'blur(4px)',
            },
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};
